import { useGlobalState } from '../GlobalState';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Drawer, Input, Spin, Typography } from 'antd';
import {
  CloseOutlined,
  CreditCardOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import applePayMark from '../../assets/images/Apple_Pay_Mark_RGB_041619.svg';
import plusCircle from '../../assets/images/PlusCircle.svg';
import axios from 'axios';
import { isNumeric } from '../../../../utils/formValidation';
import Secure3DChallengeForm from './Secure3DChallengeForm';
import { cardTypes } from './CardInput';
import useApplePay from './useApplePay';

const { Text } = Typography;

const spinIcon = (
  <LoadingOutlined
    style={{
      fontSize: 24,
    }}
    spin
  />
);

const PlusCircle = () => (
  <img src={plusCircle} alt={'+'} style={{ maxWidth: '80%' }} />
);

function PaymentMethodCardLogo({ cardTypeName = undefined }) {
  const cardType = cardTypes.find(
    (ct) => Boolean(cardTypeName) && ct.value === cardTypeName
  );
  if (!cardType?.logo) {
    return <CreditCardOutlined />;
  }
  return <img src={cardType.logo} alt={cardTypeName + ' Icon'} />;
}

function PaymentMethodCard({
  radioKey,
  text,
  selected = false,
  children,
  ...props
}) {
  return (
    <div
      id={radioKey}
      className={'payment_cards_footer__option'}
      aria-checked={selected}
      aria-labelledby={radioKey + '-label'}
      role='radio'
      {...props}
    >
      <div className={'payment_cards_footer__card mg-bottom-5'}>{children}</div>
      <label
        id={radioKey + '-label'}
        className={'gSemiBold fs-14 mg-top-15'}
        htmlFor={radioKey}
      >
        {text}
      </label>
    </div>
  );
}

const PaymentMethods = {
  NewCard: '',
  ApplePay: 'apple-pay',
  StoredCard: 'token',
};

function PaymentMethodDrawer({
  open,
  setOpen,
  setErrorPayment,
  showApplePay = false,
  amount,
  onPaymentSuccess,
  onPaymentFailure,
}) {
  const [hasApplePay, , handleApplePayClick] = useApplePay({
    onSuccess: onPaymentSuccess,
    onFailure: onPaymentFailure,
  });

  const [storedCards] = useGlobalState('storedCards');
  const [storedCardsError] = useGlobalState('storedCardsError');
  const [bookingId] = useGlobalState('bookingId');
  const [, setPaymentError] = useGlobalState('paymentError');
  const [, setPaymentSuccessRedirect] = useGlobalState(
    'paymentSuccessRedirect'
  );

  const [submitting, setSubmitting] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [cvc, setCVC] = useState(null);
  const [cvcError, setCVCError] = useState(null);
  const [secure3DFormData, setSecure3DFormData] = useState(null);

  const drawerWidthRef = useRef();

  const onCVCChanged = (e) => {
    const newValue = e.target.value;
    if (newValue.length > 4 || !isNumeric(newValue)) {
      return;
    }
    setCVC(newValue);
    setCVCError(null);
  };

  // Prevent background app scroll when drawer is open
  useEffect(() => {
    if (!document) {
      return;
    }
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => document && (document.body.style.overflow = 'unset');
  }, [open]);

  const registerPaymentWithStoredToken = useCallback(
    (bookingId, tokenId, cvc) => {
      const data = {
        booking_public_id: bookingId,
        cv2: cvc,
        stored_token_id: tokenId,
        secure_3d_window_details: {
          width: drawerWidthRef?.current?.offsetWidth || 600,
          redirect_domain: window.location.origin,
        },
        browser_details: {
          language: navigator.language,
          color_depth: window.screen.colorDepth,
          screen_height: window.screen.height,
          screen_width: window.screen.width,
          timezone: new Date().getTimezoneOffset(),
        },
      };

      const axios_config = {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        withCredentials: true,
      };

      setSubmitting(true);
      setPaymentError(null);
      setErrorPayment(null);
      setPaymentSuccessRedirect(null);
      axios
        .post('payment/direct/register_with_token', data, axios_config)
        .then((res) => {
          if (res.status === 200) {
            const res_data = res.data;
            if (res_data?.secure_3d_details) {
              setSecure3DFormData(res_data.secure_3d_details);
            } else {
              setPaymentSuccessRedirect(res_data?.redirect);
              onPaymentSuccess();
            }
          } else {
            setPaymentError(res?.data?.detail);
            setErrorPayment(res?.data?.detail);
            onPaymentFailure();
          }
        })
        .catch((err) => {
          setPaymentError(err?.response?.data?.detail);
          setErrorPayment(err?.response?.data?.detail);
          onPaymentFailure();
        })
        .finally(() => setSubmitting(false));
    },
    [setPaymentError, setErrorPayment, setPaymentSuccessRedirect, onPaymentSuccess, onPaymentFailure]
  );

  const storedCardIsSelected = selectedOption?.includes(
    PaymentMethods.StoredCard
  );

  const applePayIsSelected = selectedOption === PaymentMethods.ApplePay;

  const toggleSelectedOption = (value) =>
    setSelectedOption((prev) => (prev === value ? null : value));

  const drawerContents = (
    <>
      <div
        className={'payment_cards_footer__payment_options'}
        ref={drawerWidthRef}
        role='radiogroup'
      >
        <PaymentMethodCard
          radioKey={PaymentMethods.NewCard}
          selected={selectedOption === PaymentMethods.NewCard}
          text={'Use new card'}
          onClick={() => {
            toggleSelectedOption(PaymentMethods.NewCard);
            setOpen(false);
          }}
        >
          <PlusCircle />
        </PaymentMethodCard>

        {hasApplePay && showApplePay && (
          <PaymentMethodCard
            radioKey={PaymentMethods.ApplePay}
            selected={applePayIsSelected}
            text={'Apple Pay'}
            onClick={() => toggleSelectedOption(PaymentMethods.ApplePay)}
          >
            <img src={applePayMark} alt={'Apple Pay Logo'} />
          </PaymentMethodCard>
        )}

        {storedCardsError ? (
          <p className={'gRegular sb-color-red txt-center mg-top-20'}>
            {storedCardsError}
          </p>
        ) : (
          storedCards.map(({ card_type, end_digits }, i) => (
            <PaymentMethodCard
              radioKey={PaymentMethods.StoredCard + i}
              selected={storedCardIsSelected}
              text={
                <>
                  <span className={'gBold'}>&middot; &middot; &middot; </span>
                  {end_digits}
                </>
              }
              onClick={() =>
                toggleSelectedOption(PaymentMethods.StoredCard + i)
              }
            >
              <PaymentMethodCardLogo cardTypeName={card_type} />
            </PaymentMethodCard>
          ))
        )}
      </div>
      <div
        className={'payment_card_footer__form txt-center mg-top-15 '}
        aria-hidden={!storedCardIsSelected}
      >
        <label htmlFor={'cvc'} className={'gRegular w-full txt-center'}>
          Enter your CVC (3 digits on the back of your card)
        </label>
        <Input
          id={'cvc'}
          value={cvc}
          className={'payment_card_footer__form_item mg-auto-lr br5 txt-center'}
          onChange={onCVCChanged}
          aria-invalid={Boolean(cvcError)}
          aria-errormessage={'cvcError'}
          style={{ maxWidth: '6rem' }}
        />
        <Text
          type='danger'
          id={'cvcError'}
          className={
            'payment_card_footer__form_item__error w-full gRegular fs-13'
          }
          aria-hidden={!Boolean(cvcError)}
        >
          {cvcError}
        </Text>
      </div>
      {submitting ? (
        <div className={' txt-center w-full mg-top-20 mg-bottom-20'}>
          <Spin indicator={spinIcon} />
        </div>
      ) : (
        <Button
          id='token_payment_button'
          className={
            (!selectedOption ? 'disabled' : 'light-green-bg') +
            ' payment_card_footer__button white w-full gSemiBold fs-20 pd-10 h-full br5 mg-top-15'
          }
          disabled={!selectedOption}
          onClick={() => {
            if (storedCardIsSelected) {
              const selectedIndex = parseInt(
                selectedOption.substring(PaymentMethods.StoredCard.length)
              );
              const selectedCard = storedCards[selectedIndex];
              if (!cvc || cvc?.length < 3 || cvc?.length > 4) {
                setCVCError('Please enter a valid CVC');
                return;
              }
              registerPaymentWithStoredToken(
                bookingId?.public_id,
                selectedCard.id,
                cvc
              );
            } else if (applePayIsSelected) {
              handleApplePayClick();
            }
          }}
        >
          Pay £{amount || 'unknown'}
        </Button>
      )}
    </>
  );

  return (
    <Drawer
      height={'auto'}
      className={'payment_cards_footer'}
      placement={'bottom'}
      open={open}
      onClose={() => {
        setOpen(false);
        setSelectedOption(null);
        setCVC(null);
      }}
    >
      <CloseOutlined
        onClick={() => setOpen(false)}
        className={'fs-18 mg-bottom-20'}
      />
      <h2 className={'fs-18 gSemiBold'}>Select your payment method</h2>

      {!secure3DFormData ? (
        drawerContents
      ) : (
        <Secure3DChallengeForm
          acs_url={secure3DFormData.acs_url}
          creq={secure3DFormData.creq}
          secure_3d_session_data={secure3DFormData.secure_3d_session_data}
          onPaymentSuccess={onPaymentSuccess}
          onPaymentFailure={onPaymentFailure}
        />
      )}
    </Drawer>
  );
}

export default PaymentMethodDrawer;
